import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  documentOwnerLabels,
  documents,
  type DocumentOwnerType,
  type DocumentType,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardHeader } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

const donos = Object.entries(documentOwnerLabels) as [DocumentOwnerType, string][];

/**
 * Aba "Tipos de documento": o catalogo da empresa (RequestDocumentTypeId do OnPoint).
 *
 * O tipo marca o documento e diz em que donos e oferecido. Ele nao exige nada: quais
 * documentos sao exigidos por esteira, grupo ou dono e regra sem fonte aprovada, e a
 * exigencia continua manual. O tipo nao e excluido, so desativado.
 */
export function DocumentTypesTab() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editando, setEditando] = useState<DocumentType | 'novo' | null>(null);
  const podeManter = can('documentos.tipo.update');

  const consulta = useQuery({
    queryKey: ['document-types', 'catalogo'],
    queryFn: ({ signal }) => documents.types({ limit: 100 }, signal),
  });

  const alternar = useMutation({
    mutationFn: (tipo: DocumentType) =>
      documents.updateType(tipo.id, {
        name: tipo.name,
        description: tipo.description,
        owner_types: tipo.owner_types,
        active: !tipo.active,
      }),
    onSuccess: (tipo) => {
      toast.success(tipo.active ? 'Tipo ativado.' : 'Tipo desativado.');
      void queryClient.invalidateQueries({ queryKey: ['document-types'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <Card>
      <CardHeader
        title="Tipos de documento"
        description="Marcam o documento e dizem em que vinculos ele e oferecido. Nenhuma regra exige documento pelo tipo."
        action={
          podeManter ? (
            <Button onClick={() => setEditando('novo')}>Novo tipo</Button>
          ) : null
        }
      />
      {consulta.isError ? (
        <div className="p-5">
          <Alert tone="danger" title="Nao foi possivel carregar os tipos">
            {consulta.error.message}
          </Alert>
        </div>
      ) : (
        <DataTable
          columns={[
            {
              key: 'nome',
              header: 'Nome',
              render: (tipo) => (
                <div className="min-w-0">
                  <p className="font-medium text-ink-900">{tipo.name}</p>
                  {tipo.description ? (
                    <p className="truncate text-2xs text-ink-500">{tipo.description}</p>
                  ) : null}
                </div>
              ),
            },
            {
              key: 'donos',
              header: 'Oferecido para',
              hideBelow: 'md',
              render: (tipo) =>
                tipo.owner_types.length === 0
                  ? 'Todos os vinculos'
                  : tipo.owner_types
                      .map((dono) => documentOwnerLabels[dono])
                      .join(', '),
            },
            {
              key: 'situacao',
              header: 'Situacao',
              render: (tipo) =>
                tipo.active ? (
                  <Badge tone="success">Ativo</Badge>
                ) : (
                  <Badge tone="neutral">Inativo</Badge>
                ),
            },
            {
              key: 'acoes',
              header: 'Acoes',
              align: 'right',
              render: (tipo) =>
                podeManter ? (
                  <div className="flex justify-end gap-1">
                    <AcaoComIcone
                      acao="editar"
                      rotulo="Editar"
                      onClick={() => setEditando(tipo)}
                    />
                    <AcaoComIcone
                      acao={tipo.active ? 'desativar' : 'ativar'}
                      rotulo={tipo.active ? 'Desativar' : 'Ativar'}
                      loading={alternar.isPending && alternar.variables?.id === tipo.id}
                      onClick={() => alternar.mutate(tipo)}
                    />
                  </div>
                ) : null,
            },
          ]}
          items={consulta.data?.items ?? []}
          keyOf={(tipo) => tipo.id}
          loading={consulta.isPending}
          empty={
            <EmptyState
              icon="file"
              title="Nenhum tipo de documento"
              description="Sem tipos, o documento continua com o grupo em texto livre."
            />
          }
        />
      )}

      {editando ? (
        <TypeDialog
          tipo={editando === 'novo' ? null : editando}
          onClose={() => setEditando(null)}
        />
      ) : null}
    </Card>
  );
}

function TypeDialog({
  tipo,
  onClose,
}: {
  tipo: DocumentType | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [nome, setNome] = useState(tipo?.name ?? '');
  const [descricao, setDescricao] = useState(tipo?.description ?? '');
  const [escolhidos, setEscolhidos] = useState<DocumentOwnerType[]>(
    tipo?.owner_types ?? [],
  );

  const salvar = useMutation({
    mutationFn: () => {
      const corpo = { name: nome, description: descricao, owner_types: escolhidos };
      return tipo ? documents.updateType(tipo.id, corpo) : documents.createType(corpo);
    },
    onSuccess: () => {
      toast.success(tipo ? 'Tipo atualizado.' : 'Tipo cadastrado.');
      void queryClient.invalidateQueries({ queryKey: ['document-types'] });
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function alternarDono(dono: DocumentOwnerType, marcado: boolean) {
    setEscolhidos((atuais) =>
      marcado ? [...atuais, dono] : atuais.filter((item) => item !== dono),
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={tipo ? 'Editar tipo de documento' : 'Novo tipo de documento'}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            loading={salvar.isPending}
            disabled={nome.trim() === ''}
            onClick={() => salvar.mutate()}
          >
            Salvar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field
          label="Nome do tipo"
          required
          value={nome}
          maxLength={120}
          onChange={(event) => setNome(event.target.value)}
        />
        <Textarea
          label="Descricao do tipo"
          value={descricao}
          rows={2}
          maxLength={500}
          onChange={(event) => setDescricao(event.target.value)}
        />
        <fieldset className="flex flex-col gap-2">
          <legend className="text-xs font-semibold text-ink-700">Oferecido para</legend>
          <p className="text-2xs text-ink-500">Sem nenhum marcado, vale para todos.</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {donos.map(([dono, rotulo]) => (
              <label
                key={dono}
                className="flex items-center gap-2 text-sm text-ink-700"
              >
                <input
                  type="checkbox"
                  className="size-4"
                  checked={escolhidos.includes(dono)}
                  onChange={(event) => alternarDono(dono, event.target.checked)}
                />
                {rotulo}
              </label>
            ))}
          </div>
        </fieldset>
      </div>
    </Modal>
  );
}
